import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuthenticator } from '@aws-amplify/ui-react'
import { fetchAuthSession } from 'aws-amplify/auth'
import { useAuth } from '@hooks/auth/useAuth'
import { logger } from '@utils/logger'
import { useAuthForm } from 'core/contexts/AuthFormContext'
import { useValidateRedirectTenant } from 'core/hooks/auth/useValidateRedirectTenant'
import MainLandingPageLoader from '@components/shared/MainLandingPageLoader/MainLandingPageLoader'
import {
  ACCOUNT_DELETION_TIMEOUT_MS,
  clearDeletionIntent,
  clearAllSessionStorage,
  readDeletionSession,
  isDeletionSessionValid,
} from 'core/utils/accountDeletion'
import { isSubdomainMode, redirectToSubdomain } from '@utils/auth-mode'
import { PAGE_URL } from 'core/constants/path'
import { clearOAuthQueryParams } from 'core/utils/oauth-params'
import { AuthLayout } from 'layouts/AuthLayout/AuthLayout'

interface RedirectValidationGateProps {
  children: ReactNode
}

const ACCOUNT_DELETION_ACTION = 'delete_account'

export const RedirectValidationGate = (props: RedirectValidationGateProps) => {
  const { children } = props
  const { authStatus } = useAuthenticator()
  const { isAuthReady, session, setSession } = useAuth()
  const { cameFromSignup } = useAuthForm()
  const { validateAndRedirect } = useValidateRedirectTenant()
  const navigate = useNavigate()
  const location = useLocation()

  // Prevents concurrent validation runs when effect dependencies change rapidly
  // Used as a guard to ensure only one validation process executes at a time
  const isRunning = useRef(false)

  // Controls loading spinner visibility during redirect validation
  // true = show loader, false = render children
  // Initialized to true to show loader while checking auth status
  const [isProcessing, setIsProcessing] = useState(true)

  // Main effect: Validates redirect parameters and handles account deletion flow
  // Flow: Check auth → Ensure session tokens → Validate redirect params → Handle redirect/deletion
  useEffect(() => {
    const run = async () => {
      // ============================================
      // STEP 1: Check authentication status
      // ============================================
      const isAuthenticated = authStatus === 'authenticated'
      const lastProvider = (() => {
        try {
          return sessionStorage.getItem('auth:lastProvider') || undefined
        } catch {
          return undefined
        }
      })()

      const isFederatedLogin = Boolean(lastProvider)
      console.log('[REDIRECT_GATE] isFederatedLogin', isFederatedLogin)

      // Early return: User not authenticated
      if (!isAuthenticated) {
        console.log('[REDIRECT_GATE] Skipping validation', {
          isAuthenticated,
          hasSessionTokens: Boolean(session?.tokens?.idToken?.toString()),
          cameFromSignup,
        })

        if (!isAuthReady && isAuthenticated) {
          setIsProcessing(true)
          return
        }
        setIsProcessing(false)
        return
      }

      // ============================================
      // STEP 2: Ensure session tokens exist
      // ============================================
      let activeSession = session

      // Helper: Fetches session tokens with retry logic (more attempts for federated login)
      const ensureSessionWithTokens = async (): Promise<
        typeof session | null
      > => {
        if (activeSession?.tokens?.idToken?.toString()) {
          return activeSession
        }

        console.log('[REDIRECT_GATE] Attempting to fetch session tokens', {
          isFederatedLogin,
        })
        const maxAttempts = isFederatedLogin ? 5 : 3
        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          try {
            setIsProcessing(true)
            const refreshedSession = await fetchAuthSession({
              forceRefresh: attempt > 0 || isFederatedLogin,
            })

            if (refreshedSession?.tokens?.idToken?.toString()) {
              setSession(refreshedSession)
              console.log('[REDIRECT_GATE] Obtained session tokens', {
                attempt,
                queryParams: new URLSearchParams(
                  window.location.search
                ).toString(),
              })
              return refreshedSession
            }
            console.warn('[REDIRECT_GATE] Session fetch returned no tokens', {
              attempt,
            })
          } catch (error) {
            console.error('[REDIRECT_GATE] Failed to fetch session tokens', {
              attempt,
              error,
            })
          } finally {
            setIsProcessing(false)
          }

          await new Promise((resolve) =>
            setTimeout(resolve, isFederatedLogin ? 200 : 300)
          )
        }

        return null
      }

      activeSession = await ensureSessionWithTokens()

      // Early return: Could not obtain session tokens
      if (!activeSession?.tokens?.idToken?.toString()) {
        console.warn('[REDIRECT_GATE] Unable to obtain session tokens yet', {
          isFederatedLogin,
        })
        // Keep loader spinning for federated flows; children render for others
        setIsProcessing(isFederatedLogin)
        return
      }

      // ============================================
      // STEP 3: Clean up federated login state
      // ============================================
      // if (isFederatedLogin) {
      //   try {
      //     sessionStorage.removeItem('auth:lastProvider')
      //   } catch {}
      // }

      // ============================================
      // STEP 4: Skip validation for signup flow
      // ============================================
      if (cameFromSignup) {
        console.log(
          '[REDIRECT_GATE] User coming from signup, skipping redirect validation'
        )
        setIsProcessing(false)
        return
      }

      // ============================================
      // STEP 5: Extract email from session token
      // ============================================
      const sessionEmail = activeSession.tokens?.idToken?.payload?.email as
        | string
        | undefined

      // Early return: No email in token (anonymous user)
      if (!sessionEmail) {
        logger.log('[REDIRECT_GATE] No email in token, treating as anonymous')
        setIsProcessing(false)
        return
      }

      // ============================================
      // STEP 6: Validate redirect parameters
      // ============================================
      // Helper: Redirects to home page (handles subdomain mode)
      const redirectToHome = () => {
        if (isSubdomainMode()) {
          redirectToSubdomain('/', { replace: true })
        } else {
          window.location.replace(PAGE_URL.MAIN)
        }
      }

      // For federated login users, log early detection of redirect param
      // This ensures redirect validation will run for federated login users
      if (isFederatedLogin) {
        const urlParams = new URLSearchParams(location.search)
        const redirectParam = urlParams.get('redirect')
        if (redirectParam) {
          console.log(
            '[REDIRECT_GATE] Federated login user with redirect param detected',
            {
              redirectParam,
              lastProvider,
            }
          )
        }
      }

      // Helper: Handles account deletion flow from URL action parameter
      const handleAccountDeletion = async (actionParam: string | null) => {
        if (actionParam !== ACCOUNT_DELETION_ACTION) {
          return false
        }

        const sessionData = readDeletionSession()
        const hasRequiredData =
          sessionData.isPending && sessionData.email && sessionData.timestamp

        if (!hasRequiredData) {
          return false
        }

        const currentEmail = sessionEmail
        const storedEmail = sessionData.email

        // Email mismatch: clear deletion intent and redirect home
        if (currentEmail && storedEmail && currentEmail !== storedEmail) {
          clearDeletionIntent()
          clearAllSessionStorage()
          redirectToHome()
          return true
        }

        // Validate deletion session hasn't expired
        const isValid = isDeletionSessionValid(
          ACCOUNT_DELETION_TIMEOUT_MS,
          currentEmail
        )

        if (!isValid) {
          if (sessionData.timestamp) {
            const elapsed = Date.now() - sessionData.timestamp
            if (elapsed > ACCOUNT_DELETION_TIMEOUT_MS) {
              clearDeletionIntent()
            }
          }
          return false
        }

        // Redirect to DeleteAccount page
        navigate(PAGE_URL.AUTH_SECURITY_DELETE_ACCOUNT, { replace: true })
        // Keep processing state to prevent Auth component from rendering
        return true
      }

      // Helper: Removes query parameters from URL
      const removeParamsFromUrl = (params: string[]) => {
        try {
          const url = new URL(window.location.href)
          params.forEach((param) => url.searchParams.delete(param))
          const newSearch = url.searchParams.toString()
          const newUrl =
            url.pathname + (newSearch ? `?${newSearch}` : '') + url.hash
          window.history.replaceState({}, '', newUrl)
        } catch {
          // no-op
        }
      }

      // Main validation logic: Processes action and redirect parameters
      const validateRedirect = async () => {
        // If we're already on the delete account page, don't process and hide loader
        if (location.pathname === PAGE_URL.AUTH_SECURITY_DELETE_ACCOUNT) {
          setIsProcessing(false)
          isRunning.current = false
          return
        }

        // Prevent concurrent runs
        if (isRunning.current) {
          return
        }

        isRunning.current = true
        setIsProcessing(true)

        // Always read from current URL to get the latest params
        // This is important because params might be restored asynchronously
        let currentUrl = new URL(window.location.href)
        let urlParams = currentUrl.searchParams

        // For federated login users, check sessionStorage for saved OAuth params
        // This ensures redirect params are available even if not yet in URL
        if (isFederatedLogin) {
          try {
            const savedParams = sessionStorage.getItem('auth:oauthQueryParams')
            if (savedParams) {
              const paramsToRestore = JSON.parse(savedParams) as Record<
                string,
                string
              >
              if (paramsToRestore.redirect || paramsToRestore.action) {
                console.log(
                  '[REDIRECT_GATE] Restoring OAuth params from sessionStorage',
                  paramsToRestore
                )
                // Restore params to URL if not already present
                let needsUpdate = false
                if (
                  paramsToRestore.redirect &&
                  !currentUrl.searchParams.has('redirect')
                ) {
                  currentUrl.searchParams.set(
                    'redirect',
                    paramsToRestore.redirect
                  )
                  needsUpdate = true
                }
                if (
                  paramsToRestore.action &&
                  !currentUrl.searchParams.has('action')
                ) {
                  currentUrl.searchParams.set('action', paramsToRestore.action)
                  needsUpdate = true
                }
                if (needsUpdate) {
                  const newUrl =
                    currentUrl.pathname +
                    (currentUrl.search ? currentUrl.search : '') +
                    (currentUrl.hash || '')
                  window.history.replaceState({}, '', newUrl)
                  // Re-read from updated URL to ensure we have the latest params
                  currentUrl = new URL(window.location.href)
                  urlParams = currentUrl.searchParams
                  console.log(
                    '[REDIRECT_GATE] Updated URL with restored params',
                    newUrl
                  )
                } else {
                  console.log(
                    '[REDIRECT_GATE] Params already in URL, no update needed'
                  )
                }
              }
            }
          } catch (error) {
            console.warn(
              '[REDIRECT_GATE] Failed to restore OAuth params from sessionStorage',
              error
            )
          }
        }

        const actionParam = urlParams.get('action')
        const redirectParam = urlParams.get('redirect')

        console.log('[REDIRECT_GATE] Checking for redirect params', {
          actionParam,
          redirectParam,
          urlSearch: location.search,
          isFederatedLogin,
        })

        // Early return: No redirect parameters to process
        if (!actionParam && !redirectParam) {
          console.log(
            '[REDIRECT_GATE] No redirect params found, exiting validation'
          )
          setIsProcessing(false)
          isRunning.current = false
          return
        }

        console.log(
          '[REDIRECT_GATE] Found redirect params, proceeding with validation',
          {
            actionParam,
            redirectParam,
          }
        )

        // Explicitly ensure federated login users with redirect param are validated
        if (isFederatedLogin && redirectParam) {
          console.log(
            '[REDIRECT_GATE] Processing redirect validation for federated login user',
            {
              redirectParam,
              lastProvider,
            }
          )
        }

        let handledAccountDeletion = false

        try {
          console.log('[REDIRECT_GATE] Starting validation', {
            actionParam,
            redirectParam,
            authStatus,
            hasSession: !!activeSession,
            hasSessionTokens: Boolean(
              activeSession?.tokens?.idToken?.toString()
            ),
            sessionEmail,
            isFederatedLogin,
            lastProvider,
          })

          // Handle account deletion action
          const handledAccountDeletionResult =
            await handleAccountDeletion(actionParam)
          if (handledAccountDeletionResult) {
            handledAccountDeletion = true
            clearOAuthQueryParams()
            removeParamsFromUrl(['action'])
            // Keep isProcessing true to show loader during navigation
            console.log('[REDIRECT_GATE] Account deletion handled, redirecting')
            return
          }

          // Handle redirect parameter validation
          // This applies to both regular and federated login users
          if (redirectParam) {
            console.log('[REDIRECT_GATE] Validating redirect param', {
              redirectParam,
              isFederatedLogin,
              lastProvider,
            })
            const result = await validateAndRedirect(redirectParam)
            console.log('[REDIRECT_GATE] Redirect validation result', {
              result,
              isFederatedLogin,
            })

            if (result === 'redirected') {
              clearOAuthQueryParams()
              removeParamsFromUrl(['redirect'])
              if (isFederatedLogin) {
                sessionStorage.removeItem('auth:lastProvider')
              }
              console.log('[REDIRECT_GATE] Redirect handled, returning early', {
                isFederatedLogin,
              })
              return
            }
            console.log('[REDIRECT_GATE] No redirect match, falling through', {
              isFederatedLogin,
            })
          }

          // If redirect failed, clear params but don't navigate away
          // This prevents infinite retry loops
          clearOAuthQueryParams()
          removeParamsFromUrl(['redirect'])
          console.log('[REDIRECT_GATE] No redirect match, falling through')
        } finally {
          // Only set isProcessing to false if:
          // 1. We didn't handle account deletion, OR
          // 2. We handled it AND we're already on the delete account page
          const isOnDeleteAccountPage =
            location.pathname === PAGE_URL.AUTH_SECURITY_DELETE_ACCOUNT

          if (isOnDeleteAccountPage) {
            setIsProcessing(false)
          } else if (!handledAccountDeletion) {
            // Set processing to false when redirect validation completes
            // (unless we're still handling account deletion)
            setIsProcessing(false)
          }
          isRunning.current = false
          console.log('[REDIRECT_GATE] Processing complete', {
            handledAccountDeletion,
            isOnDeleteAccountPage,
            currentPathname: location.pathname,
          })
        }
      }

      await validateRedirect()
    }

    // Fire-and-forget: Run async validation without awaiting
    void run()
    return () => {
      isRunning.current = false
    }
  }, [
    authStatus,
    cameFromSignup,
    isAuthReady,
    location.search,
    location.pathname,
    navigate,
    session,
    setSession,
    validateAndRedirect,
  ])

  // Separate effect to handle when navigation to delete account page completes
  useEffect(() => {
    if (
      location.pathname === PAGE_URL.AUTH_SECURITY_DELETE_ACCOUNT &&
      isProcessing
    ) {
      console.log(
        '[REDIRECT_GATE] Detected navigation to delete account page, hiding loader'
      )
      setIsProcessing(false)
      isRunning.current = false
    }
  }, [location.pathname, isProcessing])

  if (isProcessing) {
    return (
      <AuthLayout className='bg-white'>
        <MainLandingPageLoader />
      </AuthLayout>
    )
  }

  return <>{children}</>
}

export default RedirectValidationGate
